// frontend/src/layouts/PublicLayout.tsx
// v3.2 - ADDED: GOLD CTA BUTTON (AUDIT TRIGGER)

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box, AppBar, Toolbar, Typography, Button, Container,
    CssBaseline, alpha, IconButton, Menu, MenuItem, Divider, Stack
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import Footer from '../components/Footer';
import PageTransition from '../modules/pricingJourney/components/PageTransition';

const PublicLayout: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // --- LÓGICA DE SCROLL (INTACTA) ---
    const [isScrolled, setIsScrolled] = useState(false);
    const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 10);
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    useEffect(() => {
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: 'auto',
        });
    }, [location.pathname]);

    const appBarStyles = {
        // Aseguramos que el navbar también use el color base correcto si hace scroll
        backgroundColor: isScrolled 
            ? alpha('#0f172a', 0.8) 
            : 'transparent',
        backdropFilter: isScrolled ? 'blur(16px)' : 'none',
        borderBottom: isScrolled ? `1px solid ${alpha('#fff', 0.08)}` : 'none',
        boxShadow: isScrolled ? '0 4px 30px rgba(0, 0, 0, 0.1)' : 'none',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        py: isScrolled ? 0 : 1 
    };

    const layoutPadding = { xs: 2, sm: 3 };

    const isProaPage = location.pathname === '/proa';
    const isIndustrialPage = location.pathname === '/industrial';

    const brandName = isIndustrialPage
        ? 'PROAIndustrial'
        : isProaPage
            ? 'PROA'
            : 'PROAPricing';

    const primaryCtaLabel = isIndustrialPage
        ? 'Hablar sobre mi fábrica'
        : isProaPage
            ? 'Ver PROAPricing'
            : 'Auditoría gratis';

    const primaryCtaTarget = isIndustrialPage
        ? 'industrial-contact'
        : isProaPage
            ? '/'
            : '/signup'; // PRC-016: «Auditoría gratis» lleva al alta.

    const primaryAccent = isIndustrialPage
        ? '#22c55e'
        : isProaPage
            ? '#38bdf8'
            : '#fcd34d';

    const primaryAccentHover = isIndustrialPage
        ? '#16a34a'
        : isProaPage
            ? '#0ea5e9'
            : '#fbbf24';

    const navigationAccent = isIndustrialPage
        ? '#22c55e'
        : '#38bdf8';

    const scrollToPublicSection = (target: string) => {
        if (target === 'top') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return true;
        }

        const element = document.getElementById(target);
        if (!element) {
            return false;
        }

        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return true;
    };

    const handleNavigation = (target: string) => {
        if (target.startsWith('/')) {
            navigate(target);
            setMobileMenuAnchor(null);
            return;
        }

        if (scrollToPublicSection(target)) {
            setMobileMenuAnchor(null);
            return;
        }

        navigate('/', {
            state: { targetSection: target },
        });

        setMobileMenuAnchor(null);
    };

    const handleLoginClick = () => {
        navigate('/login');
    };

    const navItems = isIndustrialPage
        ? [
            { label: 'Inicio', id: 'top' },
            { label: 'PROA', id: '/proa' },
            { label: 'PROAPricing', id: '/' },
        ]
        : isProaPage
            ? [
                { label: 'Visión', id: 'proa-about' },
                { label: 'Soluciones', id: 'proa-logic' },
                { label: 'Método', id: 'proa-workflow' },
                { label: 'Contacto', id: 'proa-contact' },
                { label: 'PROAPricing', id: '/' },
                { label: 'PROAIndustrial', id: '/industrial' },
            ]
            : [
                { label: 'PROA', id: '/proa' },
                { label: 'Producto', id: 'product-section' },
                { label: 'Origen', id: 'origin-section' },
                { label: 'Planes', id: 'pricing-section' },
                { label: 'Contacto', id: 'contact-form' },
                { label: 'Tecnología', id: '/methodology' },
            ];

    return (
        <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            minHeight: '100vh', 
            bgcolor: '#0f172a' 
        }}>
            <CssBaseline />
            
            <AppBar position="sticky" sx={appBarStyles} elevation={0}>
                <Container maxWidth={false} sx={{ px: layoutPadding }}>
                    {/* 
                        CAMBIO CLAVE: 
                        1. position: 'relative' para ser el ancla del menú absoluto.
                        2. justifyContent: 'space-between' para pegar logo a izq y login a der.
                    */}
                    <Toolbar disableGutters sx={{ position: 'relative', justifyContent: 'space-between' }}>
                        
                        {/* 1. LOGO (Izquierda) - Se mantiene en su sitio */}
                        <Box 
                            onClick={() => handleNavigation('top')}
                            sx={{ 
                                display: 'flex', alignItems: 'center', cursor: 'pointer',
                                // Eliminamos flexGrow y mr excesivos que ya no son necesarios
                                flexGrow: 0, 
                                transition: 'opacity 0.2s',
                                '&:hover': { opacity: 0.8 }
                            }}
                        >
                            <Box 
                                component="img" 
                                src="/logo.png" 
                                sx={{ 
                                    height: 32, mr: 1.5,
                                    filter: 'drop-shadow(0 0 8px rgba(56,189,248,0.3))' 
                                }} 
                                alt={`${brandName} Logo`}
                            />
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#f8fafc', letterSpacing: -0.5 }}>
                                {brandName}
                            </Typography>
                        </Box>

                        {/* 
                           2. NAV DESKTOP (Centro Absoluto) 
                           Sacado del flujo normal para garantizar centro matemático
                        */}
                        <Box sx={{ 
                            display: { xs: 'none', md: 'flex' }, 
                            gap: 1,
                            // MAGIA MATEMÁTICA:
                            position: 'absolute',
                            left: '50%',
                            transform: 'translateX(-50%)'
                        }}>
                            {navItems
                                .filter(
                                    (item) =>
                                        !isProaPage ||
                                        !item.id.startsWith('/')
                                )
                                .map((item) => (
                                    <Button
                                        key={item.label}
                                        onClick={() => handleNavigation(item.id)}
                                        sx={{
                                            color: '#cbd5e1',
                                            textTransform: 'none',
                                            fontWeight: 500,
                                            fontSize: '0.9rem',
                                            letterSpacing: 0.35,
                                            px: 2,
                                            borderRadius: 2,
                                            whiteSpace: 'nowrap',
                                            '&:hover': {
                                                color: navigationAccent,
                                                bgcolor: alpha(navigationAccent, 0.05),
                                            },
                                        }}
                                    >
                                        {item.label}
                                    </Button>
                                ))}
                        </Box>

                        {/* 3. RIGHT AREA (Login + CTA Gold) - Se mantiene a la derecha */}
                        <Box sx={{ flexGrow: 0, display: { xs: 'none', md: 'block' } }}>
                            {isProaPage ? (
                                <Stack direction="row" spacing={0.8}>
                                    {[
                                        {
                                            label: 'PROAPricing',
                                            target: '/',
                                            color: '#38bdf8',
                                        },
                                        {
                                            label: 'PROAIndustrial',
                                            target: '/industrial',
                                            color: '#22c55e',
                                        },
                                    ].map((item) => (
                                        <Button
                                            key={item.label}
                                            variant="text"
                                            onClick={() => handleNavigation(item.target)}
                                            sx={{
                                                color: alpha(item.color, 0.84),
                                                borderRadius: 2,
                                                px: 1.4,
                                                fontWeight: 750,
                                                fontSize: '0.86rem',
                                                textTransform: 'none',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.8,
                                                whiteSpace: 'nowrap',
                                                '&:hover': {
                                                    color: item.color,
                                                    bgcolor: alpha(item.color, 0.06),
                                                },
                                            }}
                                        >
                                            <Box
                                                component="span"
                                                sx={{
                                                    width: 6,
                                                    height: 6,
                                                    borderRadius: '50%',
                                                    bgcolor: item.color,
                                                    boxShadow: `0 0 9px ${alpha(
                                                        item.color,
                                                        0.55
                                                    )}`,
                                                    flexShrink: 0,
                                                }}
                                            />

                                            {item.label}
                                        </Button>
                                    ))}
                                </Stack>
                            ) : (
                                <Stack direction="row" spacing={2}>
                                    {!isIndustrialPage && (
                                        <Button
                                            variant="text"
                                            onClick={handleLoginClick}
                                            sx={{
                                                color: '#94a3b8',
                                                borderRadius: 50,
                                                px: 2,
                                                fontWeight: 600,
                                                textTransform: 'none',
                                                '&:hover': {
                                                    color: '#f8fafc',
                                                    bgcolor: alpha('#fff', 0.05),
                                                },
                                            }}
                                        >
                                            Iniciar Sesión
                                        </Button>
                                    )}

                                    <Button
                                        variant="contained"
                                        onClick={() =>
                                            handleNavigation(primaryCtaTarget)
                                        }
                                        sx={{
                                            bgcolor: primaryAccent,
                                            color: '#0f172a',
                                            borderRadius: 50,
                                            px: 3,
                                            fontWeight: 800,
                                            fontSize: '0.9rem',
                                            textTransform: 'none',
                                            boxShadow: `0 0 20px ${alpha(
                                                primaryAccent,
                                                0.4
                                            )}`,
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                bgcolor: primaryAccentHover,
                                                transform: 'translateY(-1px)',
                                                boxShadow: `0 0 30px ${alpha(
                                                    primaryAccent,
                                                    0.6
                                                )}`,
                                            },
                                        }}
                                    >
                                        {primaryCtaLabel}
                                    </Button>
                                </Stack>
                            )}
                        </Box>

                        {/* MOBILE MENU (Icono Hamburguesa) - A la derecha en móvil */}
                        <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
                            <IconButton 
                                onClick={(e) => setMobileMenuAnchor(e.currentTarget)}
                                sx={{ color: '#f8fafc' }} edge="end"
                            >
                                <MenuIcon />
                            </IconButton>
                            <Menu
                                anchorEl={mobileMenuAnchor}
                                open={Boolean(mobileMenuAnchor)}
                                onClose={() => setMobileMenuAnchor(null)}
                                PaperProps={{ 
                                    sx: { 
                                        bgcolor: alpha('#1e293b', 0.95), 
                                        backdropFilter: 'blur(10px)',
                                        border: '1px solid rgba(255,255,255,0.1)',
                                        color: '#fff',
                                        minWidth: 200
                                    } 
                                }}
                            >
                                {isProaPage ? (
                                    <>
                                        <Box sx={{ px: 2, pt: 1.2, pb: 0.5 }}>
                                            <Typography
                                                sx={{
                                                    color: '#64748b',
                                                    fontSize: '0.65rem',
                                                    fontWeight: 900,
                                                    letterSpacing: 1.3,
                                                }}
                                            >
                                                EN ESTA PÁGINA
                                            </Typography>
                                        </Box>

                                        <MenuItem onClick={() => handleNavigation('top')}>
                                            Inicio
                                        </MenuItem>

                                        {navItems
                                            .filter((item) => !item.id.startsWith('/'))
                                            .map((item) => (
                                                <MenuItem
                                                    key={item.label}
                                                    onClick={() => handleNavigation(item.id)}
                                                >
                                                    {item.label}
                                                </MenuItem>
                                            ))}

                                        <Divider
                                            sx={{
                                                borderColor: 'rgba(255,255,255,0.1)',
                                                my: 1,
                                            }}
                                        />

                                        <Box sx={{ px: 2, pt: 0.5, pb: 0.5 }}>
                                            <Typography
                                                sx={{
                                                    color: '#64748b',
                                                    fontSize: '0.65rem',
                                                    fontWeight: 900,
                                                    letterSpacing: 1.3,
                                                }}
                                            >
                                                SOLUCIONES
                                            </Typography>
                                        </Box>

                                        {navItems
                                            .filter((item) => item.id.startsWith('/'))
                                            .map((item) => {
                                                const itemAccent =
                                                    item.label === 'PROAIndustrial'
                                                        ? '#22c55e'
                                                        : '#38bdf8';

                                                return (
                                                    <MenuItem
                                                        key={item.label}
                                                        onClick={() =>
                                                            handleNavigation(item.id)
                                                        }
                                                    >
                                                        <Stack
                                                            direction="row"
                                                            spacing={1}
                                                            alignItems="center"
                                                        >
                                                            <Box
                                                                sx={{
                                                                    width: 6,
                                                                    height: 6,
                                                                    borderRadius: '50%',
                                                                    bgcolor: itemAccent,
                                                                    boxShadow: `0 0 9px ${alpha(
                                                                        itemAccent,
                                                                        0.55
                                                                    )}`,
                                                                }}
                                                            />

                                                            <Typography
                                                                sx={{
                                                                    color: itemAccent,
                                                                    fontWeight: 800,
                                                                }}
                                                            >
                                                                {item.label}
                                                            </Typography>
                                                        </Stack>
                                                    </MenuItem>
                                                );
                                            })}

                                        {/* El acceso de sesión se mantiene únicamente en PROAPricing. */}
                                    </>
                                ) : (
                                    <>
                                        {navItems.map((item) => (
                                            <MenuItem
                                                key={item.label}
                                                onClick={() => handleNavigation(item.id)}
                                            >
                                                {item.label}
                                            </MenuItem>
                                        ))}

                                        <Divider
                                            sx={{
                                                borderColor: 'rgba(255,255,255,0.1)',
                                                my: 1,
                                            }}
                                        />

                                        <MenuItem
                                            onClick={() =>
                                                handleNavigation(primaryCtaTarget)
                                            }
                                        >
                                            <Typography
                                                sx={{
                                                    color: primaryAccent,
                                                    fontWeight: 800,
                                                }}
                                            >
                                                {primaryCtaLabel}
                                            </Typography>
                                        </MenuItem>

                                        {!isIndustrialPage && (
                                            <MenuItem onClick={handleLoginClick}>
                                                <Typography sx={{ color: '#94a3b8' }}>
                                                    Iniciar Sesión
                                                </Typography>
                                            </MenuItem>
                                        )}
                                    </>
                                )}
                            </Menu>
                        </Box>

                    </Toolbar>
                </Container>
            </AppBar>

            {/* Resto del layout sin cambios */}
            <Container
                component="main"
                maxWidth={false}
                disableGutters
                sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}
            >
                {/* PRC-031: fundido breve entre páginas públicas (sin animar cabecera ni pie). */}
                <PageTransition /> 
            </Container>

            <Footer />
        </Box>
    );
};

export default PublicLayout;